import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { V2AdminListProfileChatsResponse } from '@workspace/api-backend'
import { v2AdminListProfileChatsOptions } from '@workspace/api-backend/react-query'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellPrimary, CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select'
import { useState } from 'react'
import { chatAttribution } from '../../../features/admin/lib/profile-chat-attribution'

/**
 * The corpus: what investors actually ask a profile.
 *
 * This is the only page in the product where a question is legible — the
 * founder's view of the same conversations is a k-anonymised topic count. It is
 * how the chip set gets decided from evidence, how a citation failure gets
 * diagnosed, and how a planted-prompt attempt is spotted before it shows up in
 * a metric.
 */

const LIMIT = 20

const TOPIC_OPTIONS = [
  'TRACTION',
  'TEAM',
  'MARKET',
  'FUNDRAISING',
  'PRODUCT',
  'CUSTOMERS',
  'RISKS',
  'OTHER',
] as const

type Topic = (typeof TOPIC_OPTIONS)[number]

const ANY_TOPIC = 'all'

/**
 * Which corpus the page reads. Visitor traffic is the default the page has
 * always shown; the founder's own previews are a separate list, never mixed in.
 */
const SOURCE_OPTIONS = ['VISITOR', 'FOUNDER_PREVIEW'] as const
type ChatSource = (typeof SOURCE_OPTIONS)[number]

const SOURCE_LABELS: Record<ChatSource, string> = {
  VISITOR: 'Visitors',
  FOUNDER_PREVIEW: 'Founder previews',
}

type SearchParams = {
  page?: number
  search?: string
  startupId?: string
  topic?: Topic
  flagged?: boolean
  source?: ChatSource
}

function toSource(value: unknown): ChatSource | undefined {
  return SOURCE_OPTIONS.find((option) => option === value)
}

function toTopic(value: unknown): Topic | undefined {
  return TOPIC_OPTIONS.find((option) => option === value)
}

export const Route = createFileRoute('/admin/profile-chats/')({
  component: AdminProfileChatsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
    startupId: (search.startupId as string) || undefined,
    topic: toTopic(search.topic),
    flagged: search.flagged === true || search.flagged === 'true' ? true : undefined,
    source: toSource(search.source),
  }),
})

// Generated from the backend schema (AD-001), not re-declared here.
type ConversationItem = V2AdminListProfileChatsResponse['data']['items'][number]

function formatDate(date: string | Date): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const columns: TableColumn<ConversationItem>[] = [
  {
    header: 'Conversation',
    width: '110px',
    render: (item) => (
      <Link
        to="/admin/profile-chats/$conversationId"
        params={{ conversationId: item.id }}
        className="font-mono text-xs text-primary hover:underline"
      >
        {item.id.slice(0, 8)}
      </Link>
    ),
  },
  {
    header: 'Startup',
    render: (item) => (
      <Link
        to="/admin/startups/$id"
        params={{ id: item.startupId }}
        className="text-sm font-medium text-primary hover:underline"
      >
        {item.startupName}
      </Link>
    ),
  },
  {
    header: 'Asked by',
    render: (item) => <CellPrimary>{chatAttribution(item)}</CellPrimary>,
  },
  {
    header: 'Link',
    width: '160px',
    render: (item) => <CellText>{item.linkName}</CellText>,
  },
  {
    header: 'Turns',
    width: '70px',
    render: (item) => <span className="font-medium tabular-nums">{item.turnCount}</span>,
  },
  {
    header: 'Topics',
    width: '220px',
    render: (item) => (
      <div className="flex flex-wrap gap-1">
        {item.topics.map((topic) => (
          <Badge key={topic} variant="secondary" size="sm">
            {topic}
          </Badge>
        ))}
      </div>
    ),
  },
  {
    header: 'Flag',
    width: '90px',
    render: (item) =>
      item.flagged ? (
        <Badge variant="destructive" size="sm">
          Check
        </Badge>
      ) : (
        <CellText>{'—'}</CellText>
      ),
  },
  {
    header: 'Last question',
    width: '200px',
    render: (item) => <CellText>{formatDate(item.lastTurnAt)}</CellText>,
  },
]

function AdminProfileChatsPage() {
  const { page = 1, search, startupId, topic, flagged, source = 'VISITOR' } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')

  const chatsQuery = useQuery(
    v2AdminListProfileChatsOptions({
      query: {
        page,
        limit: LIMIT,
        search: search || undefined,
        startupId,
        topic,
        hasCitationFailure: flagged,
        source,
      },
    })
  )

  const items = (chatsQuery.data?.data?.items ?? []) as ConversationItem[]
  const total = chatsQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {SOURCE_OPTIONS.map((option) => (
            <Button
              key={option}
              variant={source === option ? 'default' : 'outline'}
              size="sm"
              onClick={() =>
                // Page 1: the corpora have different lengths, so keeping the
                // page number lands the reader on an empty list.
                navigate({ search: { page: 1, search, startupId, topic, flagged, source: option } })
              }
            >
              {SOURCE_LABELS[option]}
            </Button>
          ))}
        </div>
        <div className="min-w-[280px] flex-1">
          <SearchInput
            placeholder="Search startups and question text..."
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={() =>
              navigate({
                search: {
                  page: 1,
                  search: searchInput || undefined,
                  startupId,
                  topic,
                  flagged,
                  source,
                },
              })
            }
          />
        </div>
        <Select
          value={topic ?? ANY_TOPIC}
          onValueChange={(value) =>
            navigate({
              search: {
                page: 1,
                search,
                startupId,
                topic: toTopic(value),
                flagged,
                source,
              },
            })
          }
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All topics" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY_TOPIC}>All topics</SelectItem>
            {TOPIC_OPTIONS.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={flagged ? 'default' : 'outline'}
          size="sm"
          onClick={() =>
            navigate({
              search: {
                page: 1,
                search,
                startupId,
                topic,
                flagged: flagged ? undefined : true,
                source,
              },
            })
          }
        >
          Flagged only
        </Button>
      </div>
      <DataTable
        columns={columns}
        data={items}
        isLoading={chatsQuery.isLoading}
        emptyMessage="No profile chats yet."
        keyExtractor={(item) => item.id}
        minWidth="1180px"
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) =>
          navigate({ search: { page: p, search, startupId, topic, flagged, source } })
        }
      />
    </div>
  )
}
